import { CHARGE_TYPE, PRICING_MODEL } from '@monorepo/shared/schemas/common'
import { Home } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { TaxesSection } from '@/shared/components/taxes/TaxesSection'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { toast } from '@/shared/lib/toast'
import type { ContractLineItem } from '../../services/contractService'
import {
  type ContractAddon,
  type ContractDiscountResponse,
  type ContractResponse,
  type ContractTaxResponse,
  contractService,
} from '../../services/contractService'
import type { Customer } from '../../services/customerService'
// Service imports
import { customerService } from '../../services/customerService'
import { type Plan, plansService } from '../../services/plansService'
import type { EditableCharge } from '../../types/customerDetail.types'
import { AddonsSection } from './AddonsSection'
import { CancelContractDialog } from './CancelContractDialog'
import { ChargesSection } from './ChargesSection'
// Component imports
import { ContractPreviewCard } from './ContractPreviewCard'
import { DiscountsSection } from './DiscountsSection'
import { SubscriptionDetails } from './SubscriptionDetails'

interface ViewEditContractProps {
  customerId: string
  contractId: string
}

// V1 Simplified - Removed hardcoded addon/discount/tax data (not needed for read-only view)

// Extended line item type that includes fields returned by the API but not in the base schema
interface ExtendedLineItem extends ContractLineItem {
  amount?: number
  package_price?: number | null
  package_size?: number | null
  action_type?: string
  charge_type?: string
}

// Tier data from API responses
interface ApiTier {
  min_units?: number
  max_units?: number | null
  unit_price?: number | string
}

// Utility function to map contract line items to editable charges
function mapLineItemToEditableCharge(
  rawLineItem: ContractLineItem
): EditableCharge {
  const lineItem = rawLineItem as ExtendedLineItem
  // Map tiers if they exist - check both metadata.tiers and direct tiers field
  const tierData =
    (lineItem.tiers as ApiTier[] | null) ||
    (lineItem.metadata as Record<string, unknown>)?.tiers
  const mappedTiers = tierData
    ? (tierData as ApiTier[]).map(tier => ({
        firstUnit: tier.min_units,
        lastUnit: tier.max_units === null ? 999999999 : tier.max_units,
        perUnitPrice: parseFloat(String(tier.unit_price)) || 0,
        minUnits: tier.min_units,
        maxUnits: tier.max_units,
        unitPrice: tier.unit_price, // Keep original for backward compatibility
        unit_price: tier.unit_price, // Add API format too
      }))
    : null

  const itemType = lineItem.type as string
  return {
    id: lineItem.id,
    category:
      itemType === 'charge' && lineItem.charge_type === CHARGE_TYPE.one_time
        ? 'fixed'
        : itemType === 'charge' &&
            lineItem.charge_type === CHARGE_TYPE.recurring
          ? 'recurring'
          : itemType === 'agent_action'
            ? 'agent'
            : itemType === 'feature'
              ? 'feature'
              : 'feature', // fallback
    type: itemType,
    name: lineItem.name,
    description: lineItem.description || '',
    unitPrice: lineItem.unit_price ?? lineItem.amount ?? 0,
    originalPrice:
      lineItem.original_price || lineItem.unit_price || lineItem.amount || 0,
    billingTiming: (lineItem.billing_timing || 'arrears') as
      | 'advance'
      | 'arrears',
    isEdited: lineItem.is_override || false,
    pricingModel:
      lineItem.pricing_model === PRICING_MODEL.flat
        ? PRICING_MODEL.flat
        : lineItem.pricing_model === PRICING_MODEL.tiered
          ? PRICING_MODEL.tiered
          : lineItem.pricing_model === PRICING_MODEL.volume
            ? PRICING_MODEL.volume
            : lineItem.pricing_model === PRICING_MODEL.package
              ? PRICING_MODEL.package
              : PRICING_MODEL.flat, // fallback
    includedUnits: lineItem.included_units || 0,
    minCommitment: lineItem.minimum_commitment || 0,
    agentId: lineItem.agent_id ?? undefined,
    signalId: lineItem.signal_id ?? undefined,
    signalName: lineItem.name,
    packagePrice: lineItem.package_price ?? null,
    packageSize: lineItem.package_size ?? null,
    kind: lineItem.action_type as EditableCharge['kind'],
    tiers: mappedTiers ?? undefined,
    // Additional mappings for different field names that might come from API
    included_units: lineItem.included_units,
    included_quantity: lineItem.included_units,
    minimum_commitment: lineItem.minimum_commitment,
    unit_price: lineItem.unit_price,
  }
}

export function ViewEditContract({
  customerId,
  contractId,
}: ViewEditContractProps) {
  // V1 Simplified - Navigation not needed
  const { setBreadcrumbs } = useBreadcrumbs()

  // Core state
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [contract, setContract] = useState<ContractResponse | null>(null)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([])
  const [editableCharges, setEditableCharges] = useState<EditableCharge[]>([])
  const [loading, setLoading] = useState(true)
  // V1 Simplified - No saving or error state needed

  // V1 Simplified - No edit mode needed

  // V1 Simplified - Form state only for display (read-only)
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [isForever, setIsForever] = useState(true)
  const [currency, setCurrency] = useState('USD')

  // V1 Simplified - Display only contract features (no editing)
  const [addons, setAddons] = useState<ContractAddon[]>([])
  const [discounts, setDiscounts] = useState<ContractDiscountResponse[]>([])
  const [taxes, setTaxes] = useState<ContractTaxResponse[]>([])
  const [activeChargeTab, setActiveChargeTab] = useState('fixed')

  // Cancellation dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  // Breadcrumbs setup
  const breadcrumbsContent = useMemo(() => {
    if (!customer || !contract) return null

    const contractName = contract.name || contract.plan?.name || contract.id

    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to='/customers'>Customers</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to={`/customers/${customerId}`}>
              {customer.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink to={`/customers/${customerId}?tab=contracts`}>
              Contracts
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{contractName}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }, [customer, contract, customerId])

  useEffect(() => {
    if (breadcrumbsContent) {
      setBreadcrumbs(breadcrumbsContent)
    }
  }, [setBreadcrumbs, breadcrumbsContent])

  // Load contract and related data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      try {
        // Load customer
        const foundCustomer = await customerService.getCustomerById(customerId)
        if (foundCustomer) {
          setCustomer(foundCustomer)
        }

        // Load available plans (for edit mode)
        try {
          const plans = await plansService.getPlans('plan', {
            status: 'active',
          })
          setAvailablePlans(Array.isArray(plans) ? plans : [])
        } catch {
          setAvailablePlans([])
        }

        // Load contract data in parallel
        const [
          contractDetails,
          lineItems,
          contractAddons,
          contractDiscounts,
          contractTaxes,
        ] = await Promise.all([
          contractService.getContractById(contractId),
          contractService.getLineItems(contractId),
          contractService.getAddons(contractId),
          contractService.getDiscounts(contractId),
          contractService.getTaxes(contractId),
        ])

        // Combine the data
        const completeContract = {
          ...contractDetails,
          line_items: lineItems,
          addons: contractAddons,
          discounts: contractDiscounts,
          taxes: contractTaxes,
        }
        setContract(completeContract)

        // Set form values from contract
        setSelectedPlanId(contractDetails.plan_id)
        setStartDate(new Date(contractDetails.start_date))
        setEndDate(
          contractDetails.end_date
            ? new Date(contractDetails.end_date)
            : undefined
        )
        setIsForever(!contractDetails.end_date)
        setCurrency(contractDetails.currency)

        // Load plan details
        if (contractDetails.plan_id) {
          try {
            const planDetails = await plansService.getPlanById(
              contractDetails.plan_id
            )
            if (planDetails) {
              setSelectedPlan(planDetails)

              // Convert contract line items to editable charges using utility function
              const charges: EditableCharge[] = (lineItems || []).map(
                mapLineItemToEditableCharge
              )
              setEditableCharges(charges)
            }
          } catch {
            // Plan details failed to load
          }
        }

        // Populate contract addons, discounts, and taxes from parallel API calls
        setAddons(contractAddons || [])
        setDiscounts(contractDiscounts || [])
        setTaxes(contractTaxes || [])
      } catch {
        toast.error(
          'Failed to load contract',
          'Unable to load contract data. Please refresh the page and try again.'
        )
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [customerId, contractId])

  // V1 Simplified - No plan selection change handling needed (read-only)

  // Removed breadcrumbs hook usage to avoid object rendering issues

  // V1 Simplified - Status color function removed (not needed)

  // V1 Simplified - No edit handlers needed (contracts are immutable)

  const handleContractAction = async (
    action: 'activate' | 'pause' | 'cancel' | 'resume'
  ) => {
    if (!contract) return

    // For cancel action, show dialog instead of immediate cancellation
    if (action === 'cancel') {
      setShowCancelDialog(true)
      return
    }

    setActionLoading(action)
    try {
      switch (action) {
        case 'activate':
          await contractService.activateContract(contract.id)
          toast.success(
            'Contract Activated',
            'Contract has been successfully activated.'
          )
          break
        case 'pause':
          await contractService.pauseContract(contract.id)
          toast.success(
            'Contract Paused',
            'Contract has been successfully paused.'
          )
          break
        case 'resume':
          await contractService.activateContract(contract.id) // Resume by activating
          toast.success(
            'Contract Resumed',
            'Contract has been successfully resumed.'
          )
          break
      }

      // Reload contract data to get updated status
      const updatedContract = await contractService.getContractById(contractId)
      setContract(updatedContract)
    } catch (error) {
      toast.error(
        `Failed to ${action} contract`,
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'
      )
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancelConfirm = async (
    reason: string,
    cancellationType: 'immediate' | 'end_of_period'
  ) => {
    if (!contract) return

    setCancelling(true)
    try {
      await contractService.cancelContract(
        contract.id,
        reason,
        cancellationType
      )

      // Reload contract data to get updated status and canceled_at date
      const updatedContract = await contractService.getContractById(contractId)
      setContract(updatedContract)

      toast.success(
        'Contract Cancelled',
        `Contract has been successfully cancelled ${cancellationType === 'immediate' ? 'immediately' : 'at the end of the billing period'}.`
      )
      setShowCancelDialog(false)
    } catch (error) {
      toast.error(
        'Failed to cancel contract',
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred. Please try again.'
      )
      // Keep dialog open on error so user can retry
    } finally {
      setCancelling(false)
    }
  }

  if (loading) {
    return (
      <div className='px-8 pt-4 pb-8'>
        <div className='flex items-center justify-center min-h-[400px]'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4'></div>
            <p>Loading contract details...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='max-w-[1600px] mx-auto px-8 pt-1 pb-8 space-y-6'>
      {/* V1 Simplified - Only Status Change Actions */}
      <div className='flex justify-end'>
        <div className='flex gap-2'>
          {/* Contract Actions based on status */}
          {contract && contract.status === 'draft' && (
            <Button
              variant='default'
              onClick={() => handleContractAction('activate')}
              disabled={actionLoading === 'activate'}
            >
              {actionLoading === 'activate'
                ? 'Activating...'
                : 'Activate Contract'}
            </Button>
          )}

          {contract && contract.status === 'active' && (
            <>
              <Button
                variant='outline'
                onClick={() => handleContractAction('pause')}
                disabled={actionLoading === 'pause'}
              >
                {actionLoading === 'pause' ? 'Pausing...' : 'Pause Contract'}
              </Button>
              <Button
                variant='outline'
                onClick={() => handleContractAction('cancel')}
                disabled={actionLoading !== null}
              >
                Cancel Contract
              </Button>
            </>
          )}

          {contract && contract.status === 'suspended' && (
            <Button
              variant='default'
              onClick={() => handleContractAction('resume')}
              disabled={actionLoading === 'resume'}
            >
              {actionLoading === 'resume' ? 'Resuming...' : 'Resume Contract'}
            </Button>
          )}
        </div>
      </div>

      {/* Contract Status Information */}
      {contract &&
        (contract.status as string) === 'canceled' &&
        contract.canceled_at && (
          <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
            <h3 className='text-sm font-medium text-red-900 mb-2'>
              Contract Cancelled
            </h3>
            <p className='text-sm text-red-800'>
              This contract was cancelled on{' '}
              <strong>
                {new Date(contract.canceled_at).toLocaleDateString()} at{' '}
                {new Date(contract.canceled_at).toLocaleTimeString()}
              </strong>
              .
              {!!contract.metadata?.cancellation_reason && (
                <span>
                  {' '}
                  Reason:{' '}
                  <strong>
                    {String(contract.metadata.cancellation_reason)}
                  </strong>
                </span>
              )}
            </p>
          </div>
        )}

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Left Column - Contract Details */}
        <div className='lg:col-span-2 space-y-6'>
          {/* Subscription Details - Read Only with selective editing */}
          <div className='space-y-4'>
            <SubscriptionDetails
              availablePlans={availablePlans}
              selectedPlanId={selectedPlanId}
              setSelectedPlanId={() => {}} // V1: All changes disabled
              selectedPlan={selectedPlan}
              startDate={startDate}
              setStartDate={() => {}} // V1: All changes disabled
              endDate={endDate}
              setEndDate={() => {}} // V1: All changes disabled
              isForever={isForever}
              setIsForever={() => {}} // V1: All changes disabled
              currency={currency}
              setCurrency={() => {}} // V1: All changes disabled
              contract={contract ?? undefined}
              customer={customer ?? undefined}
              readonly={true} // V1: Always read-only
            />
          </div>

          {/* Charges Section */}
          <div className='space-y-4'>
            <ChargesSection
              selectedPlan={selectedPlan}
              editableCharges={editableCharges}
              activeChargeTab={activeChargeTab}
              setActiveChargeTab={setActiveChargeTab}
              onChargeUpdate={() => {}} // V1: No price updates allowed
              onEditCharge={undefined} // V1: No charge editing
            />
          </div>

          {/* V1 Simplified - Read-only Addons Section */}
          <div className='space-y-4'>
            <AddonsSection
              mode='edit'
              contractId={contractId}
              viewOnly={true}
            />
          </div>

          {/* V1 Simplified - Read-only Discounts Section */}
          <div className='space-y-4'>
            <DiscountsSection discounts={discounts as never} />
          </div>

          {/* V1 Simplified - Read-only Taxes Section */}
          <div className='space-y-4'>
            <TaxesSection taxes={taxes as never} showActions={false} />
          </div>
        </div>

        {/* Right Column - Contract Preview */}
        <div className='space-y-6'>
          {contract && customer && (
            <ContractPreviewCard
              customer={customer}
              selectedPlan={
                selectedPlan as unknown as
                  | import('@/features/plans/types/plan.types').Plan
                  | null
              }
              editableCharges={editableCharges}
              addons={
                addons as unknown as Array<{
                  plan_id: string
                  name?: string
                  quantity?: number
                }>
              }
              discounts={
                discounts as unknown as Array<{
                  name: string
                  type: 'percentage' | 'fixed_amount'
                  percentage_off?: number
                  amount_off?: number
                  discount?: number
                  amount?: number
                }>
              }
              taxes={taxes}
              startDate={startDate}
              endDate={endDate}
              isForever={isForever}
              currency={currency}
            />
          )}
        </div>
      </div>

      {/* Cancel Contract Dialog */}
      <CancelContractDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        contractName={contract?.name || contract?.id || 'Unknown Contract'}
        onConfirm={handleCancelConfirm}
        isLoading={cancelling}
      />
    </div>
  )
}
